<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import { api } from "$lib/api/client";
  import { categories } from "$lib/stores/categories.svelte";
  import { fmtBRL } from "$lib/utils/format";
  import {
    Chart,
    LineController, LineElement, PointElement,
    BarController, BarElement,
    DoughnutController, ArcElement,
    CategoryScale, LinearScale, TimeScale,
    Tooltip, Legend, Filler,
  } from "chart.js";

  Chart.register(
    LineController, LineElement, PointElement,
    BarController, BarElement,
    DoughnutController, ArcElement,
    CategoryScale, LinearScale, TimeScale,
    Tooltip, Legend, Filler,
  );
  Chart.defaults.font.family = "system-ui, -apple-system, sans-serif";
  Chart.defaults.responsive = true;
  Chart.defaults.maintainAspectRatio = false;

  type Tx = {
    id: string; date: string; amount: number; kind: "income" | "expense" | "transfer";
    category_id: string | null; status: string;
  };

  let txs = $state<Tx[]>([]);
  let loading = $state(true);

  async function load() {
    loading = true;
    try {
      await categories.ensureSeeded();
      const all: Tx[] = [];
      let cursor: string | null = null;
      // Paginate up to ~3000 most recent transactions (15 pages × 200).
      for (let i = 0; i < 15; i++) {
        const qs = `limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`;
        const r = await api<{ items: Tx[]; next_cursor: string | null }>(`/transactions?${qs}`);
        all.push(...r.items.map((t) => ({ ...t, amount: Number(t.amount) })));
        if (!r.next_cursor) break;
        cursor = r.next_cursor;
      }
      txs = all;
    } finally {
      loading = false;
    }
  }

  $effect(() => { load(); });

  $effect(() => {
    function onWsChange() { load(); }
    window.addEventListener("takite:workspace-changed", onWsChange);
    return () => window.removeEventListener("takite:workspace-changed", onWsChange);
  });

  // ---------- aggregations ----------
  const now = new Date();

  const stats = $derived.by(() => {
    let income = 0, expense = 0, count = txs.length;
    let pendingCount = 0;
    const m = now.getMonth(), y = now.getFullYear();
    let monthInc = 0, monthExp = 0;
    for (const t of txs) {
      if (t.kind === "income") income += t.amount;
      else if (t.kind === "expense") expense += t.amount;
      if (t.status === "pending") pendingCount++;
      const d = new Date(t.date + "T00:00:00");
      if (d.getMonth() === m && d.getFullYear() === y) {
        if (t.kind === "income") monthInc += t.amount;
        else if (t.kind === "expense") monthExp += t.amount;
      }
    }
    return {
      total: count,
      income, expense, balance: income - expense,
      monthIncome: monthInc, monthExpense: monthExp,
      monthBalance: monthInc - monthExp,
      pendingCount,
    };
  });

  // 6-month series (last 6 months including current)
  const monthlySeries = $derived.by(() => {
    const labels: string[] = [];
    const incomes: number[] = [];
    const expenses: number[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      labels.push(d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }));
      incomes.push(0);
      expenses.push(0);
    }
    for (const t of txs) {
      const d = new Date(t.date + "T00:00:00");
      const diff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (diff < 0 || diff > 5) continue;
      const idx = 5 - diff;
      if (t.kind === "income") incomes[idx] = (incomes[idx] ?? 0) + t.amount;
      else if (t.kind === "expense") expenses[idx] = (expenses[idx] ?? 0) + t.amount;
    }
    return { labels, incomes, expenses };
  });

  // top categories (expenses)
  const topCategories = $derived.by(() => {
    const map = new Map<string, number>();
    for (const t of txs) {
      if (t.kind !== "expense" || !t.category_id) continue;
      map.set(t.category_id, (map.get(t.category_id) ?? 0) + t.amount);
    }
    const arr = [...map.entries()]
      .map(([id, total]) => ({
        id, total,
        name: categories.list.find((c) => c.id === id)?.name ?? "—",
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
    return arr;
  });

  // weekday heatmap (expenses)
  const weekdayHeat = $derived.by(() => {
    const arr = new Array(7).fill(0) as number[];
    for (const t of txs) {
      if (t.kind !== "expense") continue;
      const d = new Date(t.date + "T00:00:00").getDay();
      arr[d] = (arr[d] ?? 0) + t.amount;
    }
    const max = Math.max(1, ...arr);
    return arr.map((v) => ({ value: v, ratio: v / max }));
  });

  // count-up animation for headline
  let displayBalance = $state(0);
  $effect(() => {
    const target = stats.monthBalance;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { displayBalance = target; return; }
    const start = displayBalance;
    const dur = 700;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      displayBalance = start + (target - start) * eased;
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  // ---------- chart canvases ----------
  let lineCanvas: HTMLCanvasElement | undefined = $state();
  let donutCanvas: HTMLCanvasElement | undefined = $state();
  let barCanvas: HTMLCanvasElement | undefined = $state();
  let lineChart: Chart | null = null;
  let donutChart: Chart | null = null;
  let barChart: Chart | null = null;

  function isDark() { return document.documentElement.classList.contains("dark"); }

  function getColors() {
    const dark = isDark();
    return {
      grid: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      text: dark ? "#d4d4d8" : "#3f3f46",
      income: "#10b981",
      expense: "#ef4444",
      brand: "#6366f1",
      palette: ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#a855f7", "#84cc16"],
    };
  }

  $effect(() => {
    if (loading || !lineCanvas) return;
    const c = getColors();
    lineChart?.destroy();
    lineChart = new Chart(lineCanvas, {
      type: "line",
      data: {
        labels: monthlySeries.labels,
        datasets: [
          {
            label: "Receitas",
            data: monthlySeries.incomes,
            borderColor: c.income,
            backgroundColor: "rgba(16,185,129,0.12)",
            tension: 0.35,
            fill: true,
            pointRadius: 3,
          },
          {
            label: "Despesas",
            data: monthlySeries.expenses,
            borderColor: c.expense,
            backgroundColor: "rgba(239,68,68,0.10)",
            tension: 0.35,
            fill: true,
            pointRadius: 3,
          },
        ],
      },
      options: {
        scales: {
          x: { grid: { color: c.grid }, ticks: { color: c.text } },
          y: {
            grid: { color: c.grid },
            ticks: { color: c.text, callback: (v) => fmtBRL(Number(v)) },
          },
        },
        plugins: {
          legend: { labels: { color: c.text } },
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtBRL(Number(ctx.parsed.y))}` } },
        },
      },
    });
  });

  $effect(() => {
    if (loading || !donutCanvas) return;
    const c = getColors();
    donutChart?.destroy();
    donutChart = new Chart(donutCanvas, {
      type: "doughnut",
      data: {
        labels: topCategories.map((c) => c.name),
        datasets: [{
          data: topCategories.map((c) => c.total),
          backgroundColor: topCategories.map((_, i) => c.palette[i % c.palette.length]),
          borderWidth: 0,
        }],
      },
      options: {
        cutout: "62%",
        plugins: {
          legend: { position: "right", labels: { color: c.text, boxWidth: 12 } },
          tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${fmtBRL(Number(ctx.parsed))}` } },
        },
      },
    });
  });

  $effect(() => {
    if (loading || !barCanvas) return;
    const c = getColors();
    barChart?.destroy();
    barChart = new Chart(barCanvas, {
      type: "bar",
      data: {
        labels: topCategories.map((c) => c.name),
        datasets: [{
          label: "Total",
          data: topCategories.map((c) => c.total),
          backgroundColor: c.palette[0],
          borderRadius: 6,
        }],
      },
      options: {
        indexAxis: "y",
        scales: {
          x: { grid: { color: c.grid }, ticks: { color: c.text, callback: (v) => fmtBRL(Number(v)) } },
          y: { grid: { display: false }, ticks: { color: c.text } },
        },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => fmtBRL(Number(ctx.parsed.x)) } },
        },
      },
    });
  });

  // simple 3-month projection: avg of last 3 months
  const projection = $derived.by(() => {
    const last3Inc = monthlySeries.incomes.slice(-3);
    const last3Exp = monthlySeries.expenses.slice(-3);
    const avgInc = last3Inc.reduce((a, b) => a + b, 0) / 3;
    const avgExp = last3Exp.reduce((a, b) => a + b, 0) / 3;
    return { avgIncome: avgInc, avgExpense: avgExp, avgBalance: avgInc - avgExp };
  });

  const weekdayLabels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
</script>

<Navbar />
<main class="max-w-7xl mx-auto px-4 py-6 space-y-6">
  <header>
    <h1 class="text-2xl font-bold">Insights</h1>
    <p class="text-muted text-sm">Sua história financeira em gráficos.</p>
  </header>

  {#if loading}
    <div class="card text-center text-muted py-12">Carregando dados…</div>
  {:else if txs.length === 0}
    <div class="card text-center text-muted py-12">
      Sem transações ainda. Cadastre algumas para ver insights.
    </div>
  {:else}
    <!-- Hero headline -->
    <section class="card overflow-hidden relative">
      <div class="absolute inset-0 opacity-10 pointer-events-none"
           style="background: radial-gradient(circle at 20% 0%, var(--brand, #6366f1) 0%, transparent 60%)"></div>
      <div class="relative">
        <div class="text-sm text-muted">Saldo deste mês</div>
        <div class="text-4xl sm:text-5xl font-extrabold tabular-nums"
             class:text-emerald-600={stats.monthBalance >= 0}
             class:text-red-600={stats.monthBalance < 0}>
          {fmtBRL(displayBalance)}
        </div>
        <div class="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><span class="text-muted">Receitas:</span> <strong class="text-emerald-600">{fmtBRL(stats.monthIncome)}</strong></div>
          <div><span class="text-muted">Despesas:</span> <strong class="text-red-600">{fmtBRL(stats.monthExpense)}</strong></div>
          <div><span class="text-muted">Pendentes:</span> <strong>{stats.pendingCount}</strong></div>
          <div><span class="text-muted">Total registros:</span> <strong>{stats.total}</strong></div>
        </div>
      </div>
    </section>

    <!-- 6-month line -->
    <section class="card">
      <h2 class="font-semibold mb-3">Últimos 6 meses</h2>
      <div class="h-64">
        <canvas bind:this={lineCanvas}></canvas>
      </div>
    </section>

    <!-- Donut + Bar -->
    <section class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="card">
        <h2 class="font-semibold mb-3">Top categorias (despesas)</h2>
        <div class="h-64">
          <canvas bind:this={donutCanvas}></canvas>
        </div>
      </div>
      <div class="card">
        <h2 class="font-semibold mb-3">Ranking</h2>
        <div class="h-64">
          <canvas bind:this={barCanvas}></canvas>
        </div>
      </div>
    </section>

    <!-- Weekday heatmap -->
    <section class="card">
      <h2 class="font-semibold mb-3">Despesas por dia da semana</h2>
      <div class="grid grid-cols-7 gap-2">
        {#each weekdayHeat as d, i}
          <div class="text-center">
            <div class="text-xs text-muted mb-1">{weekdayLabels[i]}</div>
            <div
              class="rounded-md py-3 transition-colors"
              style="background: rgba(99, 102, 241, {0.1 + d.ratio * 0.7});"
              title={fmtBRL(d.value)}
            >
              <div class="text-xs font-semibold">{fmtBRL(d.value)}</div>
            </div>
          </div>
        {/each}
      </div>
    </section>

    <!-- Projection -->
    <section class="card">
      <h2 class="font-semibold mb-3">Projeção dos próximos 3 meses</h2>
      <p class="text-sm text-muted mb-3">Baseada na média dos últimos 3 meses.</p>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div class="rounded-lg p-3 border" style="border-color: var(--border)">
          <div class="text-xs text-muted">Receita média/mês</div>
          <div class="text-lg font-bold text-emerald-600">{fmtBRL(projection.avgIncome)}</div>
        </div>
        <div class="rounded-lg p-3 border" style="border-color: var(--border)">
          <div class="text-xs text-muted">Despesa média/mês</div>
          <div class="text-lg font-bold text-red-600">{fmtBRL(projection.avgExpense)}</div>
        </div>
        <div class="rounded-lg p-3 border" style="border-color: var(--border)">
          <div class="text-xs text-muted">Saldo médio/mês</div>
          <div class="text-lg font-bold" class:text-emerald-600={projection.avgBalance >= 0}
               class:text-red-600={projection.avgBalance < 0}>
            {fmtBRL(projection.avgBalance)}
          </div>
        </div>
      </div>
    </section>
  {/if}
</main>
